import { useState, useEffect, useRef } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
	Button,
	Caption,
	H3,
	InputField,
	Loader,
	SmallText,
	Text,
	ModulePanel
} from '@components';
import { BUTTON_TYPES, BUTTON_SIZE, EfficiencyOpportunity, STATIC_ADMIN_SHIPMENT_STATUS } from '@types';
import { EfficiencyModuleOpen, EfficiencyRefs, EfficiencyFocusRef } from '@state';
import { useEOSFetchShipments } from '@api';
import {
	runEfficiencyRec,
	todayMidnight, addDays,
	downloadEfficiencyCSV, getRef,
} from '@utils';
import styles from './ModuleEfficiencyRecommendation.module.scss';
import { EfficiencyCard } from './EfficiencyCard';

const EOS_FIELDS = [
	'id', 'reference',
	'original_pickup_on', 'original_dropoff_on',
	'cargos', 'req_truck_type', 'req_truck_size',
	'temperature', 'freight_mode', 'cargo_type',
	'master_shipment.shipper.company.business_name',
	'master_shipment.shipper.cost_centre_name',
	'job_list_id',
];

export const ModuleEfficiencyRecommendation = () => {
	const [isOpen, setIsOpen] = useRecoilState(EfficiencyModuleOpen);
	const [isRunning, setIsRunning] = useState(false);
	const [results, setResults] = useState<EfficiencyOpportunity[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [refSearch, setRefSearch] = useState('');
	const setEfficiencyRefs = useSetRecoilState(EfficiencyRefs);
	const [focusRef, setFocusRef] = useRecoilState(EfficiencyFocusRef);

	// Ref so the focusRef effect can read the latest results without being a dep
	const resultsRef = useRef(results);
	useEffect(() => { resultsRef.current = results; }, [results]);

	const { fetchShipments } = useEOSFetchShipments();

	const handleRun = async () => {
		setIsRunning(true);
		setError(null);
		try {
			const today = todayMidnight();
			const cutoff = addDays(today, 14);
			const toDateInt = (d: Date) =>
				d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

			const res = await fetchShipments({
				payload: {
					fields: EOS_FIELDS, q: '*', page: '1', per_page: '100',
					filter_by: {
						id: '',
						type: 'and',
						args: {
							operands: [
								{ type: '=', args: { field: 'shipment_status.for_admin', value: STATIC_ADMIN_SHIPMENT_STATUS.PENDING } },
								{ type: '>=', args: { field: 'original_pickup_on', value: toDateInt(today) } },
								{ type: '<=', args: { field: 'original_pickup_on', value: toDateInt(cutoff) } },
							]
						}
					},
					sort_by: [{ field: 'original_pickup_on', direction: 'asc' }],
				}
			});

			const unallocated = (res?.data?.hits || []).map((h) => h.document);
			const opportunities = runEfficiencyRec(unallocated as Parameters<typeof runEfficiencyRec>[0]);
			setResults(opportunities);
			setEfficiencyRefs(opportunities.flatMap((opp) => opp.group.map((s) => getRef(s))));
		} catch (e) {
			setError(e instanceof Error ? e.message : 'An error occurred');
		} finally {
			setIsRunning(false);
		}
	};

	// When another panel requests focus on a specific ref, pre-populate search and auto-run if needed
	useEffect(() => {
		if (!focusRef) return;
		setRefSearch(focusRef);
		setFocusRef(null);
		if (!resultsRef.current) handleRun();
	}, [focusRef]);

	const filteredResults = (results || []).filter((opp) => {
		if (!refSearch.trim()) return true;
		const terms = refSearch.toLowerCase().split(',').map((t) => t.trim()).filter(Boolean);
		return opp.group.some((s) => terms.some((t) => getRef(s).toLowerCase().includes(t)));
	});

	if (!isOpen) return null;

	return (
		<ModulePanel
			title='Efficiency Recommendation'
			Icon={InventoryOutlinedIcon}
			onClose={() => setIsOpen(false)}
			onRun={handleRun}
			isRunning={isRunning}
			moduleId='efficiency'
		>
			{error && <Text text={error} color='var(--color-error)' />}

			{!results && !isRunning && (
				<Text>
					Groups LTL Pallet shipments picking up in the next 14 days into consolidation opportunities.
				</Text>
			)}

			{isRunning && (
				<div className={styles.loading}>
					<Loader size={18} />
					<Text>Loading shipment data…</Text>
				</div>
			)}

			{results && !isRunning && (
				<>
					<div className={styles.stats}>
						<div className={styles.stat}>
							<H3>{results.length}</H3>
							<Caption>Opportunities</Caption>
						</div>
					</div>

					<div className={styles.filterArea}>
						<InputField
							name='refSearch'
							value={refSearch}
							onChange={(e) => setRefSearch(e.target.value)}
							placeholder='Search shipment refs… separate with commas'
							onClearInput={() => setRefSearch('')}
							stretch
							keepErrorSpace={false}
						/>
					</div>

					<div className={styles.statBar}>
						<SmallText>
							{filteredResults.length} consolidation group{filteredResults.length !== 1 ? 's' : ''}
						</SmallText>
						<Button
							text='CSV'
							onClick={() => downloadEfficiencyCSV(results)}
							type={BUTTON_TYPES.TERTIARY}
							size={BUTTON_SIZE.SMALL}
							Icon={FileDownloadIcon}
						/>
					</div>

					{results.length === 0 && (
						<Text>No consolidation opportunities found in the next 14 days.</Text>
					)}

					{refSearch && filteredResults.length === 0 && results.length > 0 && (
						<Text>No opportunities contain a shipment matching your search.</Text>
					)}

					{filteredResults.map((opp, idx) => (
						<EfficiencyCard key={idx} opportunity={opp} index={idx} refSearch={refSearch} />
					))}
				</>
			)}
		</ModulePanel>
	);
};
